import React, { useEffect, useState, useRef } from "react";
import { fetchIEReport } from "../../api/ie.api";

const fmtINR = (n) =>
  Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function IEReport({ societyId, fyId, financialYear }) {
  const [report, setReport]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const printRef              = useRef();

  useEffect(() => {
    if (!societyId || !fyId || !financialYear) return;
    loadReport();
  }, [societyId, fyId, financialYear]);

  const loadReport = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await fetchIEReport(societyId, fyId, financialYear);
      setReport(res.data);
    } catch (err) {
      setError("Failed to generate I&E report. Please try again.");
      console.error("IE Report error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div className="ie-loading">
        <div className="ie-spinner"></div>
        Generating report...
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="ie-alert error"><span>⚠</span><span>{error}</span></div>
        <button className="ie-btn" onClick={loadReport}>Try Again</button>
      </div>
    );
  }

  if (!report) return null;

  const {
    income,
    expenditure,
    totalIncome,
    totalExpenditure,
    surplusDeficit,
    isSurplus,
    grandTotal,
    journalCount,
  } = report;

  const incomeEntries      = Object.entries(income      || {});
  const expenditureEntries = Object.entries(expenditure || {});

  // Build equal-length rows for side-by-side display
  const maxRows = Math.max(incomeEntries.length, expenditureEntries.length);
  const rows    = Array.from({ length: maxRows }, (_, i) => ({
    exp: expenditureEntries[i] || null,
    inc: incomeEntries[i]      || null,
  }));

  // FY display e.g. "2025-26" → "01-04-2025 To 31-03-2026"
  const fyParts  = financialYear.split("-");
  const fyStart  = fyParts[0] ? `01-04-${fyParts[0]}` : "";
  const fyEnd    = fyParts[1] ? `31-03-20${fyParts[1]}` : "";

  return (
    <div>

      {/* ── Report actions bar ───────────────────────────────────────────── */}
      <div style={{
        display: "flex", justifyContent: "space-between",
        alignItems: "center", marginBottom: 16,
      }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600, color: "#2a221c" }}>
            Income &amp; Expenditure Account
          </div>
          <div style={{ fontSize: 12, color: "#9a8f85", marginTop: 2 }}>
            {journalCount} journal adjustment{journalCount !== 1 ? "s" : ""} applied
          </div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="ie-btn" onClick={loadReport}>↻ Refresh</button>
          <button className="ie-btn primary" onClick={handlePrint}>
            🖨 Print / PDF
          </button>
        </div>
      </div>

      {/* ── The actual I&E report ────────────────────────────────────────── */}
      <div className="ie-report-wrapper" ref={printRef}>

        {/* Dark title bar */}
        <div className="ie-report-title-bar">
          <h2>Income &amp; Expenditure Account</h2>
          <p>For the year {fyStart} to {fyEnd}</p>
        </div>

        {/* Dual-column table — exact format from reference image */}
        <table className="ie-report-table">
          <colgroup>
            <col style={{ width: 90  }} /> {/* Prev year - exp */}
            <col style={{ width: "auto" }} /> {/* Expenditure label */}
            <col style={{ width: 100 }} /> {/* Curr year - exp */}
            <col style={{ width: 3   }} /> {/* Divider */}
            <col style={{ width: 90  }} /> {/* Prev year - inc */}
            <col style={{ width: "auto" }} /> {/* Income label */}
            <col style={{ width: 100 }} /> {/* Curr year - inc */}
          </colgroup>
          <thead>
            <tr>
              <th style={{ textAlign: "right" }}>Prev Year</th>
              <th className="label-col">Expenditure</th>
              <th>Curr Year</th>
              <th className="divider-col"></th>
              <th style={{ textAlign: "right" }}>Prev Year</th>
              <th className="label-col">Income</th>
              <th>Curr Year</th>
            </tr>
          </thead>
          <tbody>

            {/* Gap row */}
            <tr className="section-gap">
              <td colSpan={3}></td>
              <td className="divider-col"></td>
              <td colSpan={3}></td>
            </tr>

            {/* Main data rows */}
            {rows.map((row, idx) => (
              <tr key={idx}>
                {/* Expenditure side */}
                <td className="amt-prev">{row.exp ? "—" : ""}</td>
                <td className={row.exp ? "label-col" : "label-col"}>
                  {row.exp ? row.exp[0] : ""}
                </td>
                <td className="amt">
                  {row.exp ? fmtINR(row.exp[1]) : ""}
                </td>

                <td className="divider-col"></td>

                {/* Income side */}
                <td className="amt-prev">{row.inc ? "—" : ""}</td>
                <td className="label-col">
                  {row.inc ? row.inc[0] : ""}
                </td>
                <td className="amt">
                  {row.inc ? fmtINR(row.inc[1]) : ""}
                </td>
              </tr>
            ))}

            {/* Gap before surplus/deficit */}
            <tr className="section-gap">
              <td colSpan={3}></td>
              <td className="divider-col"></td>
              <td colSpan={3}></td>
            </tr>

            {/* Surplus / Deficit row */}
            {isSurplus ? (
              // Surplus appears on Expenditure side (income > expense)
              <tr className="surplus-row">
                <td className="amt-prev">—</td>
                <td className="label-col surplus-amt">Surplus for the Year</td>
                <td className="amt surplus-amt">{fmtINR(Math.abs(surplusDeficit))}</td>
                <td className="divider-col"></td>
                <td className="amt-prev"></td>
                <td className="label-col"></td>
                <td className="amt"></td>
              </tr>
            ) : (
              // Deficit appears on Income side (expense > income)
              <tr className="deficit-row">
                <td className="amt-prev"></td>
                <td className="label-col"></td>
                <td className="amt"></td>
                <td className="divider-col"></td>
                <td className="amt-prev">—</td>
                <td className="label-col deficit-amt">Deficit for the Year</td>
                <td className="amt deficit-amt">{fmtINR(Math.abs(surplusDeficit))}</td>
              </tr>
            )}

            {/* Gap before grand total */}
            <tr className="section-gap">
              <td colSpan={3}></td>
              <td className="divider-col"></td>
              <td colSpan={3}></td>
            </tr>

            {/* Grand total row — both sides equal */}
            <tr className="grand-total-row">
              <td className="amt-prev">—</td>
              <td style={{
                fontWeight: 600, letterSpacing: "0.05em",
                textTransform: "uppercase", fontSize: 11, color: "#f7f3ef",
              }}>
                Total
              </td>
              <td className="amt">{fmtINR(grandTotal)}</td>
              <td className="divider-col" style={{ background: "#1a140e", borderColor: "#3a2e22" }}></td>
              <td className="amt-prev">—</td>
              <td style={{
                fontWeight: 600, letterSpacing: "0.05em",
                textTransform: "uppercase", fontSize: 11, color: "#f7f3ef",
              }}>
                Total
              </td>
              <td className="amt">{fmtINR(grandTotal)}</td>
            </tr>

          </tbody>
        </table>

        {/* Footer with signatures */}
        <div className="ie-report-footer">
          <div className="ie-report-footer-note">
            Prepared on accrual basis as per Maharashtra Co-operative Societies Act, 1960.<br />
            Figures in previous year column are for comparison only.
          </div>
          <div className="ie-sig-row">
            <div className="ie-sig-block">
              <div className="ie-sig-line">Hon. Secretary</div>
            </div>
            <div className="ie-sig-block">
              <div className="ie-sig-line">Hon. Treasurer</div>
            </div>
            <div className="ie-sig-block">
              <div className="ie-sig-line">Auditor</div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
